import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { QrCode, ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "June 2026";
const CONTACT_EMAIL = "support@commonqr.com";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>Terms of Service — CommonQR</title>
        <meta name="description" content="CommonQR Terms of Service — the rules and conditions for using the CommonQR platform." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Header */}
      <div className="border-b border-border sticky top-0 bg-card/95 backdrop-blur-sm z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <a className="inline-flex items-center gap-2 group">
              <QrCode className="w-6 h-6 text-indigo-600" />
              <span className="font-bold text-lg text-foreground group-hover:text-indigo-600 transition-colors">
                CommonQR
              </span>
            </a>
          </Link>
          <Link href="/">
            <a className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </a>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* Title */}
        <div className="mb-10">
          <p className="text-sm font-medium text-indigo-600 mb-2">Legal</p>
          <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
          <p className="text-muted-foreground">Effective date: {EFFECTIVE_DATE}</p>
          <p className="text-muted-foreground mt-1">Operated by: Vigneswaran S, trading as CommonQR</p>
        </div>

        <div className="space-y-8">

          {/* Intro */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6">
            <p className="text-foreground/80 leading-relaxed">
              Please read these Terms of Service carefully before using CommonQR. By creating an
              account or using the platform, you agree to be bound by these terms. If you do not
              agree, do not use CommonQR.
            </p>
          </div>

          <Section title="1. About CommonQR">
            <p>
              CommonQR ("we", "us", "our", "the platform") is a QR profile service operated by
              Vigneswaran S, trading as CommonQR. CommonQR allows users to create a permanent
              digital identity accessible via a unique QR code at commonqr.com/username.
            </p>
            <p className="mt-3">
              A formal legal entity registration is in progress. Until registration is complete,
              all legal obligations under these terms are the personal responsibility of
              Vigneswaran S.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>To use CommonQR you must:</p>
            <ul>
              <li>Be at least 13 years of age</li>
              <li>Provide accurate and truthful information when creating your account</li>
              <li>Have the legal capacity to enter into a binding agreement in your jurisdiction</li>
              <li>Not be prohibited from using the platform under any applicable law</li>
            </ul>
            <p className="mt-3">
              By using CommonQR, you represent and warrant that you meet all of the above eligibility requirements.
            </p>
          </Section>

          <Section title="3. Your Account">
            <p>
              When you register, you are responsible for maintaining the confidentiality of your
              login credentials. You are responsible for all activity that occurs under your account.
              Notify us immediately at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>{" "}
              if you suspect unauthorised access to your account.
            </p>
            <p className="mt-3">
              You may not share your account, sell access to your account, or allow others to use
              your account. One person or legal entity may not maintain more than one free account.
            </p>
          </Section>

          <Section title="4. Username Policy">
            <SubSection title="4.1 Permanent Username">
              <p>
                Your username is permanent and becomes your QR profile URL at commonqr.com/username.
                Once set, it cannot be changed. Choose carefully — your QR code is linked to this URL forever.
              </p>
            </SubSection>
            <SubSection title="4.2 Username is a Licence, Not Ownership">
              <p>
                CommonQR grants you a revocable, non-exclusive, non-transferable licence to use a
                username on the platform. CommonQR retains absolute and irrevocable ownership of all
                usernames on the platform. A username does not constitute property of the registered
                user and may not be sold, transferred, or assigned to any third party without the
                express written consent of CommonQR.
              </p>
            </SubSection>
            <SubSection title="4.3 Reserved Usernames">
              <p>
                Certain usernames are permanently reserved for platform operation, brand protection,
                legal compliance, geographic names, government entities, and verified trademark holders.
                CommonQR maintains a Reserved Username Registry which is updated periodically.
                Registration of a reserved username is not permitted. CommonQR may reclaim any username
                found to have been registered in violation of this policy at any time without notice or compensation.
              </p>
            </SubSection>
            <SubSection title="4.4 Trademark Priority">
              <p>
                CommonQR reserves the absolute right to transfer any username to a verified trademark
                owner following successful completion of the CommonQR Trademark Verification Process,
                regardless of the registration date of the existing username holder. The existing holder
                will be notified and offered an alternative username. No compensation shall be payable
                to the existing holder in connection with such transfer.
              </p>
            </SubSection>
            <SubSection title="4.5 Bad Faith Registration">
              <p>
                Registration of a username with the intent to sell it to a trademark owner, to
                impersonate a brand or public figure, to mislead consumers, or to engage in
                cybersquatting constitutes bad faith registration and will result in immediate account
                termination without refund of any fees paid. CommonQR may report evidence of bad faith
                registration to relevant trademark authorities and law enforcement agencies.
              </p>
            </SubSection>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to use CommonQR to:</p>
            <ul>
              <li>Post or share content that is illegal, harmful, threatening, abusive, harassing, defamatory, or obscene</li>
              <li>Impersonate any person, organisation, or brand</li>
              <li>Violate any intellectual property rights of others</li>
              <li>Distribute malware, viruses, or any harmful code via your QR profile</li>
              <li>Collect personal data from other users without their consent</li>
              <li>Use the platform for spam, phishing, or fraudulent activity</li>
              <li>Attempt to gain unauthorised access to the platform or other users' accounts</li>
              <li>Use automated tools to scrape, crawl, or extract data from the platform</li>
              <li>Circumvent any technical measures we use to protect the platform</li>
              <li>Use the platform for any purpose that violates applicable laws or regulations</li>
            </ul>
          </Section>

          <Section title="6. Your Content">
            <p>
              You retain ownership of the content you upload to CommonQR — your profile information,
              bio, links, and avatar image. By uploading content, you grant CommonQR a worldwide,
              non-exclusive, royalty-free licence to store, display, and serve your content for the
              purpose of operating the platform.
            </p>
            <p className="mt-3">
              You are solely responsible for the accuracy and legality of content you publish on your
              CommonQR profile. CommonQR does not endorse or verify the content on user profiles.
            </p>
          </Section>

          <Section title="7. Subscription Plans & Billing">
            <SubSection title="7.1 Plans">
              <p>
                CommonQR offers Free, Pro ($5/year), Pro Plus ($15/year), and Enterprise ($99/year)
                plans. Plan features and limits are as described on the pricing page at the time of
                purchase. We reserve the right to change pricing with 30 days notice to existing subscribers.
              </p>
            </SubSection>
            <SubSection title="7.2 Payment">
              <p>
                Payments are processed by Stripe. By subscribing, you authorise CommonQR to charge
                your payment method on an annual basis. All fees are in USD and are non-refundable
                except as required by applicable law.
              </p>
            </SubSection>
            <SubSection title="7.3 Grace Period">
              <p>
                If your subscription lapses, your dashboard is locked but your public QR profile
                remains live for a 90-day grace period. After 90 days, your profile will be locked.
                Renewing at any time restores full access.
              </p>
            </SubSection>
            <SubSection title="7.4 Cancellation">
              <p>
                You may cancel your subscription at any time from your dashboard. Cancellation takes
                effect at the end of the current billing period. No partial refunds are issued for
                unused subscription periods.
              </p>
            </SubSection>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              The CommonQR platform, including its software, design, logo, trademarks, and content
              created by CommonQR, is owned by Vigneswaran S and protected by applicable intellectual
              property laws. You may not copy, reproduce, modify, or distribute any part of the
              platform without our express written permission.
            </p>
            <p className="mt-3">
              CommonQR has filed 7 patents with the Indian Patent Office (CQR-PAT-001 through
              CQR-PAT-007). Unauthorised use of the patented technologies may result in legal action.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Your use of CommonQR is also governed by our{" "}
              <Link href="/privacy">
                <a className="text-indigo-600 hover:underline">Privacy Policy</a>
              </Link>
              , which is incorporated into these Terms of Service by reference.
            </p>
          </Section>

          <Section title="10. Termination">
            <p>
              We may suspend or terminate your account at any time for violations of these Terms,
              without prior notice or liability. You may delete your account at any time from your
              dashboard settings.
            </p>
            <p className="mt-3">
              On termination, your right to use the platform ceases immediately. Your public profile
              will be taken offline. Data deletion occurs as described in our Privacy Policy.
            </p>
          </Section>

          <Section title="11. Disclaimers">
            <p>
              CommonQR is provided "as is" and "as available" without warranties of any kind, either
              express or implied. We do not warrant that the platform will be uninterrupted, error-free,
              or free of viruses or other harmful components.
            </p>
            <p className="mt-3">
              CommonQR does not verify, endorse, or take responsibility for the accuracy of content
              published on user profiles. Users are solely responsible for the content they publish.
            </p>
          </Section>

          <Section title="12. Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law, CommonQR and Vigneswaran S shall not
              be liable for any indirect, incidental, special, consequential, or punitive damages,
              including loss of profits, data, or goodwill, arising from your use of or inability to
              use the platform.
            </p>
            <p className="mt-3">
              Our total liability to you for any claim arising from these Terms or your use of the
              platform shall not exceed the amount you paid to CommonQR in the 12 months preceding
              the claim.
            </p>
          </Section>

          <Section title="13. Dispute Resolution">
            <p>
              CommonQR's Trademark Verification Process is the mandatory first step for all username
              trademark disputes. For all other disputes, we encourage resolution through direct
              communication at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>{" "}
              before pursuing formal proceedings.
            </p>
          </Section>

          <Section title="14. Governing Law">
            <p>
              These Terms are governed by the laws of India. CommonQR's trademark policies are
              consistent with the Paris Convention for the Protection of Industrial Property, the
              Agreement on Trade-Related Aspects of Intellectual Property Rights (TRIPS), and the
              WIPO Uniform Domain-Name Dispute-Resolution Policy (UDRP) principles.
            </p>
          </Section>

          <Section title="15. Changes to These Terms">
            <p>
              We may update these Terms of Service from time to time. When we make material changes,
              we will notify registered users by email and update the effective date. Your continued
              use of CommonQR after the effective date constitutes acceptance of the updated terms.
            </p>
          </Section>

          <Section title="16. Contact Us">
            <p>For any questions about these Terms:</p>
            <div className="mt-3 bg-muted rounded-xl p-5 border border-border">
              <p className="font-semibold text-foreground">CommonQR</p>
              <p className="text-muted-foreground">Operated by Vigneswaran S</p>
              <p className="text-muted-foreground mt-1">
                Email:{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <p className="text-muted-foreground">Website: commonqr.com</p>
            </div>
          </Section>

        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">© 2026 CommonQR. All rights reserved.</p>
          <div className="flex gap-6">
            <Link href="/terms">
              <a className="text-sm text-indigo-600 font-medium">Terms of Service</a>
            </Link>
            <Link href="/privacy">
              <a className="text-sm text-muted-foreground hover:text-foreground/80 transition-colors">Privacy Policy</a>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Helper components ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-xl font-bold text-foreground mb-4 pb-2 border-b border-border">{title}</h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
      <div className="text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}